import React, { useEffect, useState } from 'react';
import { Brain, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TypingIndicator from './TypingIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cleanMarkdownBold } from '@/lib/utils';
import type { MessageThinkingMetadata } from '@/types';

interface AssistantThinkingBubbleProps {
  thinking?: MessageThinkingMetadata;
}

const AssistantThinkingBubble: React.FC<AssistantThinkingBubbleProps> = ({ thinking }) => {
  const [isExpanded, setIsExpanded] = useState(Boolean(thinking?.status === 'streaming'));

  useEffect(() => {
    if (thinking?.status === 'streaming') {
      setIsExpanded(true);
      return;
    }

    if (thinking?.status === 'complete' && thinking.text?.trim()) {
      setIsExpanded(false);
    }
  }, [thinking?.status, thinking?.text]);

  if (!thinking?.enabled) {
    return null;
  }

  const hasVisibleThinking = Boolean(thinking.providerExposed && thinking.text?.trim());
  const isStreaming = thinking.status === 'streaming';

  if (!hasVisibleThinking && !isStreaming) {
    return null;
  }

  return (
    <div className="mb-3 rounded-xl border border-border/70 bg-background/80 shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
        <Brain className="h-4 w-4" />
        <span className="font-medium text-foreground/80">Thinking</span>
        {hasVisibleThinking ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto h-7 px-2 text-xs"
            onClick={() => setIsExpanded((current) => !current)}
          >
            {isExpanded ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
            {isExpanded ? 'Hide' : 'Show'}
          </Button>
        ) : null}
      </div>

      {isStreaming ? (
        <div className="flex items-center gap-2 px-3 pb-3 text-sm text-muted-foreground">
          <TypingIndicator label={hasVisibleThinking ? 'Streaming reasoning…' : 'Thinking…'} />
        </div>
      ) : null}

      {hasVisibleThinking && isExpanded ? (
        <div className="border-t border-border/60 px-3 py-3 text-sm text-foreground/80">
          <div className="prose prose-sm max-w-none dark:prose-invert">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {cleanMarkdownBold(thinking.text || '')}
            </ReactMarkdown>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AssistantThinkingBubble;
